/**
 * Audit log viewer + chain-verify action.
 *
 * The verify-chain endpoint walks the hash-chained audit table and
 * reports either ok:true or the first ChainBreak (payload_hash_mismatch /
 * prev_hash_mismatch / hash_mismatch / signature_mismatch).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AuditEntry, VerifyChainResult } from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

export function AuditPage(): JSX.Element {
  const auth = useAuth();
  const canVerify = (auth.user?.permissions ?? []).includes("audit.verify_chain");

  const entries = useQuery({
    queryKey: ["audit", "list"],
    queryFn: () => api.get<readonly AuditEntry[]>("/api/audit"),
  });

  const verify = useMutation({
    mutationFn: () => api.get<VerifyChainResult>("/api/audit/verify-chain"),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Audit log</h1>
          <p className="text-sm text-slate-500">
            Tamper-evident, hash-chained. Verify the chain on demand.
          </p>
        </div>
        {canVerify ? (
          <Button onClick={() => verify.mutate()} disabled={verify.isPending}>
            {verify.isPending ? "Verifying…" : "Verify chain"}
          </Button>
        ) : null}
      </div>

      {verify.data ? (
        <Card>
          <CardHeader>
            <CardTitle>Verification result</CardTitle>
            <Badge tone={verify.data.ok ? "success" : "danger"}>
              {verify.data.ok ? "intact" : "broken"}
            </Badge>
          </CardHeader>
          <CardBody className="text-sm space-y-1">
            <p>
              Rows checked: <span className="font-medium">{verify.data.rowsChecked}</span>
            </p>
            {verify.data.break ? (
              <div className="mt-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                <strong>Break at entry {verify.data.break.entryId}:</strong>{" "}
                {verify.data.break["reason"] as string | undefined}
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {verify.isError ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
          Chain verification failed to run.
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Recent entries</CardTitle>
          <span className="text-xs text-slate-500">{entries.data?.length ?? 0} rows</span>
        </CardHeader>
        <CardBody className="p-0">
          {entries.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : entries.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load audit log.</p>
          ) : (entries.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No audit entries yet.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>When</Th>
                  <Th>Actor</Th>
                  <Th>Action</Th>
                  <Th>Target</Th>
                  <Th>Hash</Th>
                </tr>
              </THead>
              <TBody>
                {entries.data?.map((e) => (
                  <Tr key={e.id}>
                    <Td className="text-xs text-slate-500">
                      {new Date(e.occurredAt).toLocaleString()}
                    </Td>
                    <Td className="font-mono text-xs">
                      {e.actorId ? `${e.actorId.slice(0, 8)}…` : "system"}
                    </Td>
                    <Td>{e.action}</Td>
                    <Td className="font-mono text-xs">
                      {e.targetType}/{e.targetId.slice(0, 8)}…
                    </Td>
                    <Td className="font-mono text-xs text-slate-500">{e.hash.slice(0, 10)}…</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
